import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Search, MoreHorizontal, Pencil, Trash2, KeyRound, ShieldCheck, ArrowLeft,
  UserCog, GraduationCap, UserSquare,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const ROLES = [
  { value: "admin",         label: "Amministratore", color: "bg-red-50 text-red-800 border-red-200",           icon: ShieldCheck },
  { value: "direzione",     label: "Direzione",      color: "bg-purple-50 text-purple-800 border-purple-200",  icon: ShieldCheck },
  { value: "segreteria",    label: "Segreteria",     color: "bg-blue-50 text-blue-800 border-blue-200",        icon: UserCog },
  { value: "raccolta_fondi",label: "Raccolta fondi", color: "bg-teal-50 text-teal-800 border-teal-200",        icon: UserCog },
  { value: "economato",     label: "Economato",      color: "bg-cyan-50 text-cyan-800 border-cyan-200",        icon: UserCog },
  { value: "docente",       label: "Docente",        color: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: UserSquare },
  { value: "studente",      label: "Studente",       color: "bg-amber-50 text-amber-800 border-amber-200",     icon: GraduationCap },
];
const ROLE_MAP = Object.fromEntries(ROLES.map((r) => [r.value, r]));

const EMPTY_FORM = { email: "", name: "", role: "segreteria", password: "", teacher_id: "", student_id: "", send_email: true };

export default function UsersManager() {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [delUser, setDelUser] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [createdInfo, setCreatedInfo] = useState(null);
  const [resetInfo, setResetInfo] = useState(null);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["users", { roleFilter, search }],
    queryFn: async () => {
      const params = {};
      if (roleFilter !== "all") params.role = roleFilter;
      if (search) params.search = search;
      return (await api.get("/users", { params })).data;
    },
    enabled: ["admin", "direzione"].includes(me?.role),
  });
  const { data: stats } = useQuery({
    queryKey: ["users-stats"],
    queryFn: async () => (await api.get("/users-stats/overview")).data,
    enabled: ["admin", "direzione"].includes(me?.role),
  });

  const createMut = useMutation({
    mutationFn: async (p) => (await api.post("/users", p)).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["users-stats"] });
      toast.success(`Utente ${d.email} creato`);
      setCreateOpen(false);
      if (d.temp_password) setCreatedInfo({ email: d.email, temp_password: d.temp_password });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const editMut = useMutation({
    mutationFn: async ({ id, p }) => (await api.patch(`/users/${id}`, p)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      toast.success("Utente aggiornato");
      setEditUser(null);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/users/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["users-stats"] });
      toast.success("Utente eliminato");
      setDelUser(null);
    },
    onError: (e) => { toast.error(e.response?.data?.detail || "Errore"); setDelUser(null); },
  });

  const resetMut = useMutation({
    mutationFn: async (id) => (await api.post("/users/reset-password", { user_id: id, send_email: true })).data,
    onSuccess: (d, id) => {
      toast.success("Password reimpostata");
      const u = users.find((x) => x.id === id);
      setResetInfo({ email: u?.email, temp_password: d.temp_password });
      setResetTarget(null);
    },
    onError: (e) => { toast.error(e.response?.data?.detail || "Errore"); setResetTarget(null); },
  });

  if (!["admin", "direzione"].includes(me?.role)) {
    return (
      <div className="p-10 max-w-lg mx-auto text-center">
        <ShieldCheck size={40} className="mx-auto text-stone-400 mb-3"/>
        <div className="font-medium">Accesso riservato</div>
        <div className="text-sm text-stone-500 mt-1">Solo admin e direzione possono gestire gli utenti.</div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <Link to="/impostazioni" className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-[#1C352D] mb-4">
        <ArrowLeft size={13}/> Torna alle impostazioni
      </Link>
      <PageHeader
        title="Gestione utenti"
        subtitle="Crea account per amministratori, segreteria, docenti e studenti; assegna ruoli e privilegi."
        actions={
          <Button
            data-testid="user-add-btn"
            onClick={() => setCreateOpen(true)}
            className="rounded-full bg-[#1C352D] hover:bg-[#264A3E]"
          >
            <Plus size={16} className="mr-1.5"/> Nuovo utente
          </Button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <Card className="p-4 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Totale</div>
          <div className="text-2xl font-semibold text-[#1C352D] mt-1">{stats?.total || 0}</div>
        </Card>
        {ROLES.map((r) => (
          <Card key={r.value} className="p-4 border border-stone-200 rounded-xl shadow-none">
            <div className="text-[10px] uppercase tracking-widest text-stone-500">{r.label}</div>
            <div className="text-2xl font-semibold mt-1">{stats?.by_role?.[r.value] || 0}</div>
          </Card>
        ))}
      </div>

      <Card className="border border-stone-200 rounded-xl shadow-none">
        <div className="p-4 flex flex-col md:flex-row gap-3 border-b border-stone-100">
          <div className="relative flex-1 max-w-md">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
            <Input data-testid="user-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca nome o email…" className="pl-9"/>
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-48" data-testid="user-role-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i ruoli</SelectItem>
              {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="py-10 text-center text-stone-400 text-sm">Caricamento…</div>
        ) : users.length === 0 ? (
          <div className="py-14 text-center text-stone-400 text-sm">Nessun utente trovato.</div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {users.map((u, i) => {
              const R = ROLE_MAP[u.role] || ROLE_MAP.segreteria;
              const RIcon = R.icon;
              return (
                <li key={u.id} data-testid={`user-row-${u.id}`} className="p-4 md:p-5 flex items-center gap-4 hover:bg-stone-50 animate-in-row" style={{ animationDelay: `${i * 10}ms` }}>
                  <div className={`w-10 h-10 rounded-full border grid place-items-center shrink-0 ${R.color}`}>
                    <RIcon size={16}/>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium">{u.name}</div>
                      <Badge className={`text-[10px] rounded-full border ${R.color}`}>{R.label}</Badge>
                      {u.is_disabled && <Badge className="text-[10px] bg-stone-200 text-stone-700 rounded-full">Disattivo</Badge>}
                      {u.must_change_password && <Badge className="text-[10px] bg-amber-50 text-amber-800 border-amber-200 rounded-full">Password provvisoria</Badge>}
                    </div>
                    <div className="text-[11px] text-stone-500 mt-0.5">
                      {u.email}
                      {u.linked_name && <> · Collegato a: <b>{u.linked_name}</b>{u.linked_matricola && <> ({u.linked_matricola})</>}</>}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`user-actions-${u.id}`} className="p-1.5 rounded-md hover:bg-stone-100 text-stone-500 hover:text-[#1C352D]"><MoreHorizontal size={16}/></button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48 bg-white">
                      <DropdownMenuItem onClick={() => setEditUser(u)}><Pencil size={14} className="mr-2"/>Modifica</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setResetTarget(u)}><KeyRound size={14} className="mr-2"/>Reset password</DropdownMenuItem>
                      <DropdownMenuSeparator/>
                      <DropdownMenuItem
                        disabled={u.id === me.id}
                        className="text-red-700 focus:text-red-800 focus:bg-red-50"
                        onClick={() => setDelUser(u)}
                      ><Trash2 size={14} className="mr-2"/>Elimina</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <UserFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode="create"
        submitting={createMut.isPending}
        onSubmit={(p) => createMut.mutate(p)}
      />
      <UserFormDialog
        open={!!editUser}
        onOpenChange={(v) => !v && setEditUser(null)}
        mode="edit"
        initial={editUser}
        submitting={editMut.isPending}
        onSubmit={(p) => editMut.mutate({ id: editUser.id, p })}
      />

      <AlertDialog open={!!delUser} onOpenChange={(v) => !v && setDelUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {delUser?.name}?</AlertDialogTitle>
            <AlertDialogDescription>L&apos;utente perde accesso al sistema. L&apos;anagrafica (studente/docente) resta.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="user-delete-confirm" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => delMut.mutate(delUser.id)}>Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!resetTarget} onOpenChange={(v) => !v && setResetTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reimpostare la password di {resetTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>Verrà generata una password provvisoria e inviata via email. L&apos;utente dovrà cambiarla al prossimo accesso.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-[#1C352D] hover:bg-[#264A3E]" onClick={() => resetMut.mutate(resetTarget.id)}>Reimposta</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BackupCredsDialog open={!!createdInfo} onClose={() => setCreatedInfo(null)} data={createdInfo} title="Utente creato"/>
      <BackupCredsDialog open={!!resetInfo} onClose={() => setResetInfo(null)} data={resetInfo} title="Password reimpostata"/>
    </div>
  );
}

function BackupCredsDialog({ open, onClose, data, title }) {
  if (!data) return null;
  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>L&apos;utente ha ricevuto la password via email. Copia questi dettagli come backup:</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="p-4 bg-stone-50 rounded-lg space-y-1.5 text-sm">
          <div><b>Email:</b> <span className="mono">{data.email}</span></div>
          <div><b>Password provvisoria:</b> <code className="bg-white px-2 py-0.5 rounded border border-stone-200 mono">{data.temp_password}</code></div>
        </div>
        <AlertDialogFooter>
          <AlertDialogAction data-testid="creds-ok" onClick={onClose} className="bg-[#1C352D] hover:bg-[#264A3E]">Ho capito</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function UserFormDialog({ open, onOpenChange, mode, initial, onSubmit, submitting }) {
  const [form, setForm] = useState(EMPTY_FORM);

  const { data: teachers = [] } = useQuery({
    queryKey: ["teachers-users"],
    queryFn: async () => (await api.get("/teachers")).data,
    enabled: open,
  });
  const { data: students = [] } = useQuery({
    queryKey: ["students-users"],
    queryFn: async () => (await api.get("/students", { params: { status: "attivo" } })).data,
    enabled: open,
  });

  React.useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          email: initial.email,
          name: initial.name,
          role: initial.role,
          password: "",
          teacher_id: initial.teacher_id || "",
          student_id: initial.student_id || "",
          send_email: false,
        });
      } else {
        setForm(EMPTY_FORM);
      }
    }
  }, [open, initial]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  function submit(e) {
    e.preventDefault();
    const payload = { ...form };
    if (mode === "edit") {
      delete payload.email; delete payload.send_email; delete payload.password;
    }
    if (payload.role !== "docente") payload.teacher_id = null;
    if (payload.role !== "studente") payload.student_id = null;
    if (mode === "create" && !payload.password) delete payload.password;
    onSubmit(payload);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Nuovo utente" : "Modifica utente"}</DialogTitle>
          <DialogDescription>
            {mode === "create" ? "Se non imposti una password, verrà generata automaticamente e inviata via email." : "Puoi modificare nome, ruolo e collegamenti. Per cambiare la password usa Reset."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Nome e cognome</Label>
            <Input required data-testid="user-form-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Mario Rossi"/>
          </div>
          {mode === "create" && (
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" required data-testid="user-form-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="mario@rdm.it"/>
            </div>
          )}
          <div>
            <Label className="text-xs">Ruolo</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger data-testid="user-form-role"><SelectValue/></SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => <SelectItem key={r.value} value={r.value} data-testid={`user-form-role-${r.value}`}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.role === "docente" && (
            <div>
              <Label className="text-xs">Collega a docente in anagrafica</Label>
              <Select value={form.teacher_id || "__none"} onValueChange={(v) => set("teacher_id", v === "__none" ? "" : v)}>
                <SelectTrigger data-testid="user-form-teacher"><SelectValue placeholder="Seleziona docente…"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Nessuno</SelectItem>
                  {teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {form.role === "studente" && (
            <div>
              <Label className="text-xs">Collega a studente in anagrafica</Label>
              <Select value={form.student_id || "__none"} onValueChange={(v) => set("student_id", v === "__none" ? "" : v)}>
                <SelectTrigger data-testid="user-form-student"><SelectValue placeholder="Seleziona studente…"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Nessuno</SelectItem>
                  {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} · {s.matricola}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {mode === "create" && (
            <div>
              <Label className="text-xs">Password (opzionale)</Label>
              <Input type="text" data-testid="user-form-password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="Lascia vuoto per generare automaticamente"/>
              <div className="text-[11px] text-stone-500 mt-1">Se lasci vuoto, sarà generata una password provvisoria e inviata via email.</div>
            </div>
          )}
          <DialogFooter className="mt-3">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button type="submit" disabled={submitting} data-testid="user-form-submit" className="bg-[#1C352D] hover:bg-[#264A3E]">
              {submitting ? "Salvataggio…" : mode === "create" ? "Crea utente" : "Salva modifiche"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
